import React from 'react';
import {
  Home, BookOpen, Gamepad2, TrendingUp, ShoppingCart, Trophy, Settings,
  GraduationCap, Presentation, Coins, Brain, Sprout, Flame, Zap, Shuffle,
  Search, Target, CheckCircle2, HelpCircle, RefreshCw, BarChart3, ListChecks,
  Glasses, Gem, Crown, Heart, Medal, Clock, Lightbulb, PartyPopper, Award, Skull,
  BookA,
} from 'lucide-react';

/**
 * 🟢 [图标升级·方案A] 渐变主题图标
 *
 * 用 Lucide 图标 + 全局 SVG 渐变实现「扁平渐变」风格，用于导航栏、底部 Tab
 * 以及各页面的主题/大卡片图标。渐变通过 <GradientDefs /> 在页面根节点注入一次，
 * 这里的图标以 color="url(#vg-主题)" 引用它 —— 任意 Lucide 图标都能渐变上色。
 *
 * 用法：<GradientIcon name="games" size={40} />
 * 功能性小图标（关闭/编辑/箭头等）请用 <AppIcon />（纯 currentColor）。
 */

// name → { 组件, 渐变主题 }
const ICONS = {
  // 导航
  dashboard: [Home, 'sky'],
  courses: [BookOpen, 'green'],
  games: [Gamepad2, 'purple'],
  progress: [TrendingUp, 'orange'],
  shop: [ShoppingCart, 'pink'],
  leaderboard: [Trophy, 'gold'],
  settings: [Settings, 'slate'],
  achievement: [GraduationCap, 'indigo'],
  teacher: [Presentation, 'sky'],
  coins: [Coins, 'gold'],
  // 主题/特性
  book: [BookOpen, 'green'],
  library: [BookA, 'indigo'], // 词库/词典（与 courses 的 BookOpen 区分开）
  brain: [Brain, 'purple'],
  sprout: [Sprout, 'green'],
  flame: [Flame, 'orange'],
  zap: [Zap, 'sky'],
  shuffle: [Shuffle, 'purple'],
  search: [Search, 'sky'],
  target: [Target, 'pink'],
  'check-circle': [CheckCircle2, 'green'],
  question: [HelpCircle, 'orange'],
  sync: [RefreshCw, 'sky'],
  chart: [BarChart3, 'orange'],
  tasks: [ListChecks, 'sky'],
  glasses: [Glasses, 'purple'],
  gem: [Gem, 'pink'],
  crown: [Crown, 'gold'],
  heart: [Heart, 'red'],
  medal: [Medal, 'gold'],
  clock: [Clock, 'orange'],
  lightbulb: [Lightbulb, 'gold'],
  party: [PartyPopper, 'pink'],
  award: [Award, 'gold'],
  skull: [Skull, 'slate'],
};

// 渐变主题（from → to），供 <GradientDefs /> 生成
export const GRADIENT_THEMES = {
  sky: ['#38bdf8', '#3b82f6'],
  green: ['#34d399', '#10b981'],
  purple: ['#a78bfa', '#8b5cf6'],
  orange: ['#fbbf24', '#f97316'],
  pink: ['#f472b6', '#ec4899'],
  gold: ['#fcd34d', '#f59e0b'],
  slate: ['#94a3b8', '#64748b'],
  indigo: ['#818cf8', '#6366f1'],
  red: ['#fb7185', '#ef4444'],
};

/**
 * 全局渐变定义，只需在 App 根节点渲染一次。
 * 用 objectBoundingBox，图标任意尺寸都能正确上色。
 */
export const GradientDefs = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
    <defs>
      {Object.entries(GRADIENT_THEMES).map(([key, [from, to]]) => (
        <linearGradient key={key} id={`vg-${key}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={to} />
        </linearGradient>
      ))}
    </defs>
  </svg>
);

// plain=true 时用 currentColor（跟随 text-* 颜色），用于深色/彩色背景上的白色图标；
// 默认使用渐变，适合浅色背景。
const GradientIcon = ({ name, size = 24, className = '', strokeWidth = 2.2, plain = false }) => {
  const [Cmp, theme] = ICONS[name] || ICONS.dashboard;
  return (
    <Cmp
      size={size}
      color={plain ? 'currentColor' : `url(#vg-${theme})`}
      strokeWidth={strokeWidth}
      className={className}
      aria-hidden="true"
    />
  );
};

export default GradientIcon;
